<template>
  <div id="page-container">
    <component :is="currentContainer">
      <router-view v-if="isRouterAlive" />
    </component>
  </div>
</template>

<script setup lang="ts">
import { isMobile } from "@common_lib/base/utils/src/tools"
import { sendMessage } from "@/composables/useBridge"
import { safeLocalStorage, safeSessionStorage } from "@/utils/safeStorage"

const googleAnalytics = useGoogleAnalytics()

useModalStore()
useNavbarStore()

useFraudProtectionSystem()
useReceiveMessage()
useWebsiteErrorRedirect()

// PostMessage 處理 iframe 關閉訊息
const { handleIframeCloseQuery } = usePostMessage()
handleIframeCloseQuery()

const {
  shouldLoadJBLScript,
  hasMountedJBL,
  loadJBLScript,
  initJBLWebSocket,
  fetchRecommendLine
} = useJBLWebSocket()

useChangeLine({
  shouldLoadJBLScript,
  hasMountedJBL,
  loadJBLScript,
  initJBLWebSocket,
  fetchRecommendLine
})

useChat()

const components = {
  MobileContainer: defineAsyncComponent(() => import("@/layouts/mobile/Container.vue")),
  DesktopContainer: defineAsyncComponent(() => import("@/layouts/desktop/Container.vue"))
}

const currentContainer = computed(() =>
  isMobile() ? components.MobileContainer : components.DesktopContainer
)

const websiteInfoStore = useWebsiteInfoStore()
const { isRouterAlive, state } = storeToRefs(websiteInfoStore)
const authStore = useAuthStore()
const { isLogin, certification } = storeToRefs(authStore)
const { fetchAccountTracker } = useFetchAccountTracker()
const { fetchFingerprintPro } = useFetchFingerprintPro()

const ACCOUNT_TRACKER_CALLED_KEY = "accountTrackerCalled"
const LAST_TRACKED_QUERY_PASS_KEY = "lastTrackedQueryPass"

// API 白牌每次帶不同 queryPass 進來都是新的 login，要視為新 tracking session。
// 比對 URL 帶進來的 queryPass 是否跟上次 tracking 過的不同，是的話強制清掉 guard
// 讓接下來的 watcher / onMounted 重新打一次。
const urlQueryPass = safeSessionStorage.getItem("urlQueryPass")
if (urlQueryPass) {
  const lastTrackedQueryPass = safeLocalStorage.getItem(LAST_TRACKED_QUERY_PASS_KEY)
  if (urlQueryPass !== lastTrackedQueryPass) {
    safeLocalStorage.removeItem(ACCOUNT_TRACKER_CALLED_KEY)
    safeLocalStorage.setItem(LAST_TRACKED_QUERY_PASS_KEY, urlQueryPass)
  }
}

const callTrackingAPIs = () => {
  if (safeLocalStorage.getItem(ACCOUNT_TRACKER_CALLED_KEY)) return
  safeLocalStorage.setItem(ACCOUNT_TRACKER_CALLED_KEY, "true")
  fetchAccountTracker().catch(err =>
    console.error("[App] accountTracker API failed", err)
  )
  fetchFingerprintPro().catch(err =>
    console.error("[App] fingerprintPro API failed", err)
  )
}

const shouldCallTrackingAPIs = computed(() => {
  if (state.value.userID) return true
  // Half-login：login API 已通過但 userID 尚未設定，需先完成 terms / resetPassword
  const { termsVersion, resetPassword } = certification.value
  return termsVersion.length > 0 || resetPassword
})

watch(
  shouldCallTrackingAPIs,
  should => {
    if (should) callTrackingAPIs()
    else safeLocalStorage.removeItem(ACCOUNT_TRACKER_CALLED_KEY)
  },
  { immediate: true }
)

useBridge()

onMounted(() => {
  // API 白牌：tab session 帶 queryPass 進來的話，userID 可能因 terms 待接受而延後設定，
  // 提前在 mount 時觸發一次 tracking。localStorage guard 確保不會跟 userID watcher 重複打。
  if (urlQueryPass) {
    callTrackingAPIs()
  }

  googleAnalytics.initialize()

  /**
   * APP 橋接
   */
  if (state.value.isMobileAppActive) {
    const cricketUrl = `${window.location.origin}/#/?AppHomeBtn=true`
    sendMessage(
      JSON.stringify({
        sessionId: state.value.queryPass,
        platform: "cricket",
        cricketUrl
      })
    )
    sendMessage(JSON.stringify({ isShowHomeButton: "true" }))

    if (isLogin.value) {
      sendMessage(`{"userId":"${state.value.userID}"}`)
    }
  }
})
</script>

<style lang="scss" scoped></style>
